import { ChangeState, FSM } from 'afsm';
import { Video2dContext, VideoContextErrorCode, VideoGlContext } from '../video-context';
import { MixLayout, VideoMixNode } from './mix';
import { ILogger, LocalTrackEvent } from '../../enums';
import RtcError from '../../common/rtc-error';
import ErrorCode from '../../common/error-code';
import { IOS_MAIN_VERSION } from '../../utils/environment';
import { isUndefined } from '../../utils';
import { VideoDestinationNode } from './destination';

/**
 * -1,1---------1,1
 * |             |
 * |             |
 * |             |
 *-1,-1---------1,-1
 *  */
// const positions = [
//   -1, -1, // 左下
//   -1, 1, // 左上
//   1, -1, // 右下
//   1, 1 // 右上
// ];

const positions = [
  -1, -1, // 左下
  1, -1, // 右下
  -1, 1, // 左上
  1, 1 // 右上
];
/**
 * 0,1---------1,1
 * |             |
 * |             |
 * |             |
 * 0,0---------1,0
 */
// const texCoords = [
//   0, 0, // 左下
//   0, 1, // 左上
//   1, 0, // 右下
//   1, 1 // 右上
// ];

const texCoords = [
  0, 0, // 左下
  1, 0, // 右下
  0, 1, // 左上
  1, 1 // 右上
];
export interface VideoNodeOptions {
  name?: string;
  image?: TexImageSource;
  vertexShaderSource?: string;
  fragmentShaderSource?: string;
  useDefaultProgram?: boolean;
  createTexture?: boolean;
  create2d?: boolean;
  useFbo?: boolean;
  width?: number;
  height?: number;
  matchInputSize?: boolean;
  mirrorUpAndDown?: boolean;
  autoResize?: boolean;
  logger: ILogger;
}
export enum UpdateType {
  JustUpdate,
  UseTimerOnShow,
}
export abstract class VideoNode<S extends TexImageSource = HTMLCanvasElement | OffscreenCanvas, C extends Video2dContext | VideoGlContext = Video2dContext> extends FSM<{ [VideoNode.RENDER]: [source: CanvasImageSource]; }> {
  static RENDER = 'render';
  name: string;
  sourceId = '';
  input?: VideoNode<TexImageSource, C>;
  output?: VideoNode<TexImageSource, C>;
  // lastProcessTime = 0;
  texture?: WebGLTexture;
  ctx2d: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null = null;
  fbo?: WebGLFramebuffer;
  width = 0;
  height = 0;
  x = 0;
  y = 0;
  program?: WebGLProgram;
  vertexShader?: WebGLShader;
  fragmentShader?: WebGLShader;
  totalFrames = 0;
  dropFrames = 0;
  matchInputSize = true;
  texCoordBuffer: WebGLBuffer;
  positionBuffer: WebGLBuffer;
  lastInfo: VideoNodeInfo = {
    name: '',
    timestamp: 0,
    totalFrames: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    fps: 0
  };
  cost = 0;
  _canvas: HTMLCanvasElement | OffscreenCanvas | null = null;
  protected _image: S;
  log: ILogger;
  constructor(public context: C, options: VideoNodeOptions) {
    super();
    this.context.on('disconnect', this.close, this);
    this.name = options.name;
    this.log = options.logger;
    this.matchInputSize = options.matchInputSize !== false;
    this.width = options.width || context.width;
    this.height = options.height || context.height;
    this._image = <S>options.image;
    if (context instanceof Video2dContext) {
      // Create node-level canvas for nodes that need 2d rendering (e.g. TextSourceNode, TransformNode).
      // This is independent of the global context.ctx which may be null in Worker mode.
      if (options.create2d) {
        if (typeof OffscreenCanvas === 'function' && IOS_MAIN_VERSION !== 16) {
          this._canvas = new OffscreenCanvas(this.width, this.height);
        } else {
          this._canvas = document.createElement('canvas');
          this._canvas.width = this.width;
          this._canvas.height = this.height;
        }
        this.ctx2d = this._canvas.getContext('2d');
        this._image = this._canvas as S;
      }
      return;
    }
    try {
      const gl = context.ctx!;
      this.texCoordBuffer = this.createBuffer(texCoords);
      this.positionBuffer = this.createBuffer(positions);
      if (options.createTexture !== false) {
        this.texture = gl.createTexture()!;
        this.useTexture();
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      }
      if (options.useFbo) {
        this.fbo = gl.createFramebuffer()!;
        this.useBufferFrame();
        this.useTexture();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.width, this.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texture!, 0);
      }
      if (options.useDefaultProgram) this.program = context.defaultProgam;
      else if (options.vertexShaderSource || options.fragmentShaderSource) {
        this.vertexShader = options.vertexShaderSource ? context.createShader(gl.VERTEX_SHADER, options.vertexShaderSource) : context.defaultVShader;
        this.fragmentShader = options.fragmentShaderSource ? context.createShader(gl.FRAGMENT_SHADER, options.fragmentShaderSource) : context.defaultFShader;
        this.program = context.createProgram(this.vertexShader, this.fragmentShader);
      }
    } catch (err) {
      this.context.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.CREATE_VIDEO_NODE_FAILED,
        message: `create video node ${this.name} error ${(err as any).message || err}`
      }));
    }
  }
  get image() {
    return this._image;
  }
  set image(value: S) {
    this._image = value;
  }
  createFramebuffer(texture: WebGLTexture) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    return fbo;
  }
  @ChangeState(FSM.INIT, 'connected', { sync: true })
  connect<T extends VideoNode<TexImageSource, C>>(node: T, ...args: (T extends VideoMixNode ? [MixLayout] : any[])) {
    node.addInput(this, ...args);
    this.output = node;
    return node;
  }
  addInput(node: VideoNode<TexImageSource, C>, ...args: any[]) {
    this.input = node;
    if (this.matchInputSize && node.width && node.height) this.resize(node.width, node.height);
  }
  // output -> input
  requestFrame(seq: number) {
    const t = Date.now();
    if ((this.context instanceof VideoGlContext && this.render(seq)) || (this.context instanceof Video2dContext && this.render2d(seq))) {
      this.totalFrames++;
    } else return false;
    this.cost = Date.now() - t;
    return true;
  }
  abstract render(seq: number): boolean;
  render2d(seq: number) {
    if (this.input?.requestFrame(seq)) {
      return this.draw2d(this.input.image, 0, 0, this.width, this.height);
    }
    return false;
  }
  // input -> output
  update(type: UpdateType = UpdateType.JustUpdate) {
    this.output?.update(type);
  }
  @ChangeState('connected', FSM.INIT, { ignoreError: true, sync: true })
  disconnect(...args: any[]) {
    this.output?.removeInput(this, ...args);
    delete this.output;
  }
  removeInput(node: VideoNode<TexImageSource, C>, ...args: any[]) {
    delete this.input;
  }
  @ChangeState([], 'closed', { sync: true })
  close() {
    this.context.off('disconnect', this.close, this);
    this.output?.removeInput(this);
    delete this.output;
    this.input?.disconnect();
    if (this.context instanceof VideoGlContext) {
      const gl = this.context.ctx!;
      gl.deleteBuffer(this.texCoordBuffer);
      gl.deleteBuffer(this.positionBuffer);
      if (this.fbo) gl.deleteFramebuffer(this.fbo);
      if (this.texture) gl.deleteTexture(this.texture);
      if (this.vertexShader && this.vertexShader !== this.context.defaultVShader) gl.deleteShader(this.vertexShader);
      if (this.fragmentShader && this.fragmentShader !== this.context.defaultFShader) gl.deleteShader(this.fragmentShader);
      if (this.program && this.program !== this.context.defaultProgam) gl.deleteProgram(this.program);
    }
    if (this._canvas) {
      this._canvas.width = 0;
      this._canvas.height = 0;
      this.ctx2d = null;
    }
    this.removeAllListeners();
  }
  useTexture() {
    this.useTextures(this.texture);
  }
  useInputTexture() {
    this.useTextures(this.input?.texture);
  }
  useTextures(...textures: (WebGLTexture | undefined)[]) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    textures.forEach((t, index) => {
      if (t) {
        gl.activeTexture(gl.TEXTURE0 + index);
        gl.bindTexture(gl.TEXTURE_2D, t);
      }
    });
  }
  useProgram() {
    this.context.ctx!.useProgram(this.program!);
  }
  useBufferFrame() {
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo || null);
  }
  createBuffer(data: Array<number>) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    const b = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
    return b;
  }
  setTexBuffer(data: Array<number>) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
  }
  setPosBuffer(data: Array<number>) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
  }
  changeBufferData(b: WebGLBuffer, data: Array<number>) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
  }
  setAttributes(...attributes: WebGLBuffer[]) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    attributes.forEach((b, index) => {
      gl.enableVertexAttribArray(index);
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.vertexAttribPointer(index, 2, gl.FLOAT, false, 0, 0);
    });
  }
  getVertexPoint(x: number, y: number) {
    return [
      (x / this.width) * 2 - 1,
      (y / this.height) * 2 - 1
    ];
  }
  layout2texCoords(layout: { x: number, y: number, width: number, height: number; }) {
    return [
      ...this.getVertexPoint(layout.x, layout.y), // 左上->左下
      ...this.getVertexPoint(layout.x + layout.width, layout.y), // 右上->右下
      ...this.getVertexPoint(layout.x, layout.y + layout.height), // 左下->左上
      ...this.getVertexPoint(layout.x + layout.width, layout.y + layout.height) // 右下->右上
    ];
  }
  resize(width: number, height: number) {
    if (this.width === width && this.height === height) return;
    this.width = width;
    this.height = height;
    if (this._canvas) {
      this._canvas.width = width;
      this._canvas.height = height;
    }
    if (this.texture && this.fbo) {
      this.useTexture();
      const gl = this.context.ctx as WebGL2RenderingContext;
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    }
    if (this.output && this.output.matchInputSize) this.output.resize(width, height);
  }
  draw(a_position?: WebGLBuffer, a_texCoord?: WebGLBuffer) {
    this.setAttributes(a_position || this.positionBuffer, a_texCoord || this.texCoordBuffer);
    const gl = this.context.ctx as WebGL2RenderingContext;
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  draw2d(image: TexImageSource | undefined, dx: number, dy: number, dw: number, dh: number, sx?: number, sy?: number, sw?: number, sh?: number) {
    const cropSrc = !isUndefined(sx) && !isUndefined(sy) && !isUndefined(sw) && !isUndefined(sh);
    if (this.ctx2d && image) {
      if (image instanceof ImageData) {
        if (cropSrc) this.ctx2d.putImageData(image, dx, dy, sx, sy, sw, sh);
        else this.ctx2d.putImageData(image, dx, dy);
        this.emit(VideoNode.RENDER, this.ctx2d.canvas);
      } else {
        if (cropSrc) this.ctx2d.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh);
        else this.ctx2d.drawImage(image, dx, dy, dw, dh);
        this.emit(VideoNode.RENDER, image);
      }
      if (typeof VideoFrame !== 'undefined' && image instanceof VideoFrame) {
        image.close();
      }
      return true;
    }
    return false;
  }
  drawBackGround2d(color: string | CanvasGradient | CanvasPattern) {
    if (this.ctx2d) {
      this.ctx2d.save();
      this.ctx2d.fillStyle = color;
      this.ctx2d.fillRect(0, 0, this.width, this.height);
      this.ctx2d.restore();
    }
  }
  getInfo(): { parent?: VideoNodeInfo | VideoNodeInfo[]; } & VideoNodeInfo {
    const { totalFrames, x, y, width, height, name, cost, dropFrames } = this;
    const timestamp = Date.now();
    const fps = (totalFrames - this.lastInfo.totalFrames) / ((timestamp - this.lastInfo.timestamp) / 1000) >> 0;
    this.lastInfo = { totalFrames, x, y, width, height, timestamp, fps, name, cost, dropFrames };
    return {
      parent: this.input?.getInfo(),
      ...this.lastInfo
    };
  }
  createTexture(image: TexImageSource) {
    const gl = this.context.ctx as WebGL2RenderingContext;
    const texture = gl.createTexture()!;
    this.useTextures(texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    return texture;
  }
}
export interface VideoNodeInfo {
  timestamp: number,
  name: string,
  totalFrames: number, x: number, y: number, width: number, height: number, fps: number;
  cost?: number;
  dropFrames?: number;
}
